import type { PayloadAction } from '@reduxjs/toolkit';
import { combineReducers, createSlice } from '@reduxjs/toolkit';
import { createMigrate, persistReducer } from 'redux-persist';

import type { GenericError, JsonFailure, RequestStatus } from '@skynet/data-ws-core';
import { registerErrorMessage, isOkCommand, parseResponse } from '@skynet/data-ws-core';
import { actionOnMessage } from '@skynet/data-ws-mm';

import { startAppListening } from 'src/store/middleware/listenerMiddleware';
import { balancesActions } from 'src/store/shared/balances/slice';
import { isSubAccountsSectionFrame } from 'src/store/shared/balances/type-guards';
import { tasksActions } from 'src/store/shared/tasks/slice';
import retainersProjectsReducer, { initialState as ProjectsInitialState } from './projects/slice';
import retainersAdminPanelReducer, {
  initialState as AdminPanelInitialState,
} from './admin-panel/slice';
import retainersBalancesUIReducer, { balanceTableOptsActions } from './balance-table-opts/slice';
import retainersDepthViewOptionsReducer from './depth-view-opts/slice';
import retainerTermsReducer, {
  retainerTermsActions,
  initialState as TermsInitialState,
} from './terms/slice';
import retainersKpiReducer from './kpi/slice';
import retainersThresholdReducer from './threshold/slice';
import {
  retainerOverviewReducer,
  retainerOverviewActions,
  RetainerOverviewCommands,
  initialState as RetainerOverviewInitialState,
} from '@skynet/data-retainer-overview';
import { retainersArchiveReducer, archiveInitialState } from '@skynet/data-archive';
import retainersBotReducer, {
  retainersBotActions,
  initialState as RetainersBotInitialState,
} from './bots/slice';
import { hideProject, listen } from './actions';
import { RetainerCommands } from './types';
import { RetainerTermsCommands } from './terms/types';
import { startCoinGeckoLinkRefreshListening } from 'src/store/shared/coingecko-link-refresh';
import { withResetState } from 'src/store/actions';
import {
  selectCurrentProject,
  selectDefaultProject,
  selectIsProjectExist,
  selectProjects,
  selectTargetProject,
} from 'src/store/retainers/selectors';
import { AdminPanelCommands } from 'src/store/retainers/admin-panel/types';
import { ExchangeCommands } from '@skynet/data-shared-exchanges';
import { selectExchanges } from 'src/store/shared/exchange-list/selectors';
import { skyRouter } from 'src/routes/sky-router';
import { isApplicationDomain, isNoIdSection } from 'src/hooks/useSection';
import { routerHistoryChange } from 'src/store/router/actions';
import { ApplicationDomain } from 'src/routes/types';
import { ListenSection } from 'src/hooks/useProjectSwitch/listen-sections';
import storage from 'src/store/storage';

export interface RetainersState {
  error: null | GenericError;

  // Retainers (right-hand) sidebar and header
  listenStatus: RequestStatus;
  isSidebarOpen: boolean;
  targetProject: string;
  targetExchange: string;
  overviewActiveTab: string;

  isNewProjectModalOpen: boolean;

  resHideProject: RequestStatus;
}

registerErrorMessage(RetainerCommands.HideRetainer, {
  title: 'Archive Retainer Error',
  getDescription: (json: JsonFailure) => String(json.message),
});

const allowedCommands = [
  ...Object.values(RetainerCommands),
  RetainerOverviewCommands.RetainerOverview,
];

const initialState: RetainersState = {
  error: null,
  // Loans (right-hand) sidebar and header
  listenStatus: null,
  isSidebarOpen: false,
  targetProject: '',
  targetExchange: 'balances',
  overviewActiveTab: '',
  isNewProjectModalOpen: false,
  resHideProject: null,
};

const retainersSlice = createSlice({
  name: 'retainers',
  initialState,
  reducers: {
    toggleSidebar(state) {
      state.isSidebarOpen = !state.isSidebarOpen;
    },

    setTargetProject(state, action: PayloadAction<{ projectId: string }>) {
      state.targetProject = action.payload.projectId;
    },

    setTargetExchange(state, action: PayloadAction<{ exchangeId: string }>) {
      state.targetExchange = action.payload.exchangeId || 'balances';
    },

    setTarget(state, action: PayloadAction<{ projectId: string; exchangeId?: string }>) {
      state.listenStatus = null;
      state.targetProject = action.payload.projectId;
      state.targetExchange = action.payload.exchangeId ?? 'balances';
    },

    setOverviewActiveTab(state, action: PayloadAction<string>) {
      state.overviewActiveTab = action.payload;
    },

    openNewProjectModal(state) {
      state.isNewProjectModalOpen = true;
    },

    closeNewProjectModal(state) {
      state.isNewProjectModalOpen = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(listen, (state, action) => {
      const { section } = action.payload;
      const { isInitCall } = action.meta;
      const isOverview = ['overview', 'terms'].includes(section);
      if (isInitCall) {
        if (!isOverview) {
          state.listenStatus = 'pending';
        }
      }
    });

    builder.addCase(hideProject.pending, (state) => {
      state.resHideProject = 'pending';
    });

    builder.addCase(actionOnMessage, (state, action) => {
      const { json, skipProcessing, error, errorCommand } = parseResponse(
        action.payload,
        allowedCommands,
      );

      if (skipProcessing || !json) return;

      const { command, okCommand } = json;

      if (error) {
        state.error = error;
        switch (errorCommand) {
          case RetainerCommands.HideRetainer:
            state.resHideProject = 'error';
            break;
          case undefined:
          default:
        }

        return;
      }

      if (command === RetainerCommands.Balances && !isSubAccountsSectionFrame(json)) {
        state.listenStatus = 'success';
      }

      if (isOkCommand(command)) {
        switch (okCommand) {
          case RetainerCommands.HideRetainer:
            state.resHideProject = 'success';
            break;
          default:
        }
      }
    });
  },
});

export const retainersActions = {
  ...retainersSlice.actions,
  listen,
  hideProject,
};

const balanceTableOptsConfig = {
  key: 'retainers/balanceTableOpts',
  version: 1,
  storage,
  blacklist: ['viewMode'],
};

const persistedRetainersBalancesUIReducer = persistReducer(
  balanceTableOptsConfig,
  retainersBalancesUIReducer,
);

const depthViewOptionsConfig = {
  key: 'retainers/depthViewOptions',
  version: 1,
  storage,
};

const persistedRetainersDepthViewOptionsReducer = persistReducer(
  depthViewOptionsConfig,
  retainersDepthViewOptionsReducer,
);

// `sortKey`/`hideEmptyAccounts` live on the `projects` slice (see below), not on
// `RetainersState`, so `index` itself has nothing that needs to survive a reload.
const persistedRetainersIndexReducer = withResetState(retainersSlice.reducer, initialState);

/**
 * v1 of the `retainers/sidebar` key wrapped the `index` reducer with a
 * `whitelist: ['sortKey']`, but `RetainersState` has no `sortKey`, so the key
 * only ever stored redux-persist bookkeeping. v2 moves it onto `projects`,
 * where the sidebar preferences actually live. Same key, different shape —
 * discard whatever v1 wrote instead of reconciling a foreign payload into
 * `RetainersProjectsState`.
 */
export const projectsPersistMigrations = {
  2: () => undefined,
};

const projectsPersistConfig = {
  key: 'retainers/sidebar',
  version: 2,
  storage,
  whitelist: ['sortKey', 'hideEmptyAccounts'],
  migrate: createMigrate(projectsPersistMigrations),
};

const persistedRetainersProjectsReducer = persistReducer(
  projectsPersistConfig,
  withResetState(retainersProjectsReducer, ProjectsInitialState),
);

export default combineReducers({
  index: persistedRetainersIndexReducer,
  projects: persistedRetainersProjectsReducer,
  balancesUI: persistedRetainersBalancesUIReducer,
  depthViewOptions: persistedRetainersDepthViewOptionsReducer,
  adminPanel: withResetState(retainersAdminPanelReducer, AdminPanelInitialState),
  terms: withResetState(retainerTermsReducer, TermsInitialState),
  kpi: retainersKpiReducer,
  threshold: retainersThresholdReducer,
  overview: withResetState(retainerOverviewReducer, RetainerOverviewInitialState),
  archive: withResetState(retainersArchiveReducer, archiveInitialState),
  bot: withResetState(retainersBotReducer, RetainersBotInitialState),
});

startAppListening({
  actionCreator: retainersActions.setTarget,
  effect: (_, listenerApi) => {
    listenerApi.dispatch(balancesActions.resetList());
    listenerApi.dispatch(retainersBotActions.resetSession());
    listenerApi.dispatch(tasksActions.resetSearchValue());
    listenerApi.dispatch(retainerTermsActions.resetUpdateRetainer());
    listenerApi.dispatch(balanceTableOptsActions.resetIgnoreTokens());
  },
});

// Mark the retainer overview as loading when the app starts listening for the
// overview section. This reactivity previously lived in the slice's `listen`
// extraReducer; the slice now lives in `@skynet/data-retainer-overview`, which
// cannot import the app-level `listen` action, so it is dispatched here instead.
startAppListening({
  actionCreator: listen,
  effect: (action, listenerApi) => {
    const { section } = action.payload;
    const { isInitCall } = action.meta;
    if (isInitCall && section === 'overview') {
      listenerApi.dispatch(retainerOverviewActions.setRequestPending());
    }
  },
});

/** URL sections that render the retainer terms form; both listen to `terms` (see getListenSection). */
const RETAINER_TERMS_SECTIONS = ['terms', 'terms-admin'];

// Bring the CoinGecko link back after an UpdateRetainer that changed the project.
startCoinGeckoLinkRefreshListening({
  updateCommand: RetainerTermsCommands.UpdateRetainer,
  selectTerms: (state) => state.retainers.terms,
  isTermsPageOpen: (projectID) => {
    const section = skyRouter.getSection();
    if (!section) {
      return false;
    }
    return (
      skyRouter.getDomain() === ApplicationDomain.Retainers &&
      RETAINER_TERMS_SECTIONS.includes(section) &&
      skyRouter.getEntityId() === String(projectID)
    );
  },
  listen: (projectID) =>
    retainersActions.listen({ section: ListenSection.Terms, projectID }, { isInitCall: false }),
});

// TODO: move to navigation config, or calculate from the existintg
// const CLIENT_RETAINERS_SECTION = ['overview', 'terms', 'balances', 'tasks'];
/**
 * Listen to the WS messages and applies redirect logic to retainers
 * when all the required top level (domain) data is loaded.
 */
startAppListening({
  actionCreator: actionOnMessage,
  effect: async (action, listenerApi) => {
    const domain = skyRouter.getDomain();
    const section = skyRouter.getSection();

    if (!domain || !section || domain !== ApplicationDomain.Retainers) {
      return;
    }

    const state = listenerApi.getState();
    const requiredCommands = [
      AdminPanelCommands.AdminPanel,
      RetainerCommands.Projects,
      ExchangeCommands.Exchanges,
    ];
    const { json, skipProcessing } = parseResponse(action.payload, requiredCommands);

    if (skipProcessing || !json) return;

    const hasProjects = selectProjects(state).length > 0;
    // const hasProjectsAdmin = selectProjectsAdmin(state) !== null;
    const hasExchanges = selectExchanges(state).length > 0;

    const entityId = skyRouter.getEntityId();

    if (hasProjects && hasExchanges /*&& hasProjectsAdmin */) {
      // const defaultProject = CLIENT_RETAINERS_SECTION.includes(section)
      //   ? selectDefaultProject(state)
      //   : selectDefaultAdminProject(state);

      const defaultProject = selectDefaultProject(state);

      // If current section doesn't require entityId, we skip the following checks
      if (isApplicationDomain(domain) && isNoIdSection(domain, section)) {
        if (defaultProject) {
          listenerApi.dispatch(
            retainersActions.setTargetProject({ projectId: String(defaultProject.id) }),
          );
        }
        return;
      }

      // If the section requires entityId, and the entityId is not present in the URL,
      // we redirect to the default project
      if (!entityId) {
        if (defaultProject) {
          await skyRouter.router?.navigate(
            `/${domain.toLowerCase()}/${section}/${defaultProject.id}`,
          );
        }
        return;
      }

      const hasAccess = selectIsProjectExist(entityId)(state);
      if (!hasAccess) {
        if (defaultProject) {
          listenerApi.dispatch(
            retainersActions.setTarget({ projectId: String(defaultProject.id) }),
          );
        }
        await skyRouter.router?.navigate(`/${domain.toLowerCase()}/not-found`);
        return;
      }
    }
    const projectID = selectTargetProject(state);
    if (entityId && !projectID) {
      listenerApi.dispatch(retainersActions.setTargetProject({ projectId: entityId }));
    }
  },
});

// Listen to router history change for Retainers domain.
// if URL project ID is different from the current project ID,
// and user has access to the URL project, set the URL project ID as the current one.
startAppListening({
  actionCreator: routerHistoryChange,
  effect: (action, listenerApi) => {
    const domain = skyRouter.getDomain();
    const section = skyRouter.getSection();
    const entityId = skyRouter.getEntityId();

    if (domain !== ApplicationDomain.Retainers || !section || !entityId) {
      return;
    }

    // const projectsList = CLIENT_RETAINERS_SECTION.includes(section)
    //   ? selectProjects(state)
    //   : selectProjectsAdmin(state);
    const state = listenerApi.getState();
    const currentTarget = selectCurrentProject(state);

    if (String(currentTarget?.id) !== entityId && selectIsProjectExist(entityId)(state)) {
      listenerApi.dispatch(retainersActions.setTarget({ projectId: String(entityId) }));
    }
  },
});

startAppListening({
  actionCreator: routerHistoryChange,
  effect: (_, listenerApi) => {
    const domain = skyRouter.getDomain();
    const section = skyRouter.getSection();

    // Reset terms status when user is in Retainers domain but NOT on terms page
    // or when leaving Retainers domain entirely
    if (
      domain !== ApplicationDomain.Retainers ||
      (section !== 'terms-admin' && section !== 'terms')
    ) {
      listenerApi.dispatch(retainerTermsActions.resetUpdateRetainer());
    }
  },
});
